import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Phone,
  CheckCircle,
  Sparkles,
  Factory,
  Award,
  ArrowRight,
  HeartHandshake,
} from 'lucide-react';
import { FiFacebook, FiInstagram } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';

import logo from '../assets/logo.png';
import heroRetail from '../assets/projects/Kalyan Jewellers.jpg';
import heroGate from '../assets/projects/Prithvi Pride.jpg';
import heroGlow from '../assets/projects/Younger You Face Yoga.jpg';

function useHeroReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return [ref, visible];
}

function useSectionReveal(options = {}) {
  const { threshold = 0.1, rootMargin = '0px 0px -8% 0px' } = options;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);
  return [ref, visible];
}

const brands = ['Duroflex', 'Top & Town', 'Mango Sip', 'Gina Soft Drink', 'Kalyan Jewellers'];

const servicesList = [
  'LED Sign Boards',
  'Acrylic Letters',
  'Slim LED Frames',
  'Fabric Light Boards',
  'ACP Sheet Work',
  'Building Gate Signage',
  'Terrace Signage',
  'Builder Site Branding',
  'Shop Front Sign Boards',
  'Showroom Branding',
  'Flex Printing',
  'Name Signages',
  'Interior Branding',
];

const whyChooseList = [
  '25+ Years of Industry Experience',
  'In-House Manufacturing Setup',
  'Trusted by Known Brands',
  '10+ Builder Projects Completed',
  'Professional Installation Team',
  'Serving Clients Across Mumbai',
];

function AboutHero() {
  const [ref, visible] = useHeroReveal();

  return (
    <section
      ref={ref}
      className="relative min-h-[52vh] md:min-h-[56vh] flex items-center overflow-hidden bg-surface"
      aria-labelledby="about-hero-title"
    >
      <div className="absolute inset-0 bg-gradient-hero-glow opacity-90" aria-hidden />
      <div
        className="absolute top-1/4 right-0 w-[min(80vw,480px)] h-[min(80vw,480px)] rounded-full bg-gradient-to-bl from-brand-orange/20 via-amber-500/10 to-transparent blur-3xl animate-glow-orbs"
        aria-hidden
      />
      <div
        className="absolute -bottom-20 left-0 w-[min(70vw,400px)] h-[min(70vw,400px)] rounded-full bg-gradient-to-tr from-brand-yellow/15 to-transparent blur-3xl animate-glow-orbs [animation-delay:1s]"
        aria-hidden
      />

      <div className="container relative z-10 mx-auto px-4 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 xl:gap-16 items-center">
          <div
            className={`max-w-3xl lg:max-w-none transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="inline-flex items-center gap-2 text-brand-amber text-xs md:text-sm uppercase tracking-[0.22em] font-semibold mb-5">
              <Sparkles className="w-4 h-4 text-brand-yellow shrink-0" aria-hidden />
              Kandivali · Mumbai
            </p>
            <h1
              id="about-hero-title"
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-5"
            >
              The face of your
              <span className="block text-gradient-brand mt-1 drop-shadow-[0_0_28px_rgba(245,158,11,0.2)]">
                business, built right
              </span>
            </h1>
            <div
              className={`h-1 w-24 md:w-32 rounded-full bg-gradient-brand mb-6 origin-left scale-x-0 ${
                visible ? 'animate-hero-line' : ''
              }`}
              aria-hidden
            />
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8 max-w-2xl">
              Shyam Advertising is a signage and branding studio with over{' '}
              <span className="text-white font-medium">25 years</span> on the job—design through install, for
              shops, showrooms, and builder-scale façades across Mumbai.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-7 py-3 text-sm font-bold text-black shadow-brand-glow hover:opacity-95 transition-opacity"
              >
                Work with us
                <ArrowRight className="w-4 h-4" aria-hidden />
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white hover:border-brand-amber/40 hover:bg-brand-orange/10 transition-colors"
              >
                See install photos
              </Link>
            </div>
          </div>

          {/* Desktop: story collage — logo mark + real installs */}
          <div
            className={`relative hidden lg:block min-h-[380px] xl:min-h-[440px] transition-all duration-[900ms] delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            aria-hidden
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[118%] max-w-none h-[85%] rounded-[2rem] bg-gradient-to-br from-brand-orange/22 via-brand-amber/10 to-transparent blur-3xl opacity-90 animate-glow-orbs" />
            <div className="relative mx-auto max-w-lg xl:max-w-xl">
              <div className="absolute -top-1 right-4 xl:right-8 z-30 rounded-2xl border border-white/15 bg-black/50 backdrop-blur-md p-3 shadow-xl animate-float-slow">
                <img src={logo} alt="" className="h-10 xl:h-11 w-auto object-contain opacity-95" />
              </div>

              <p className="absolute top-14 left-0 z-20 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur-md px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-yellow">
                <HeartHandshake className="w-3.5 h-3.5" />
                25+ years on site
              </p>

              <div
                className="relative mt-16 rounded-[1.35rem] overflow-hidden ring-1 ring-white/15 shadow-card-premium-hover animate-float-slow"
                style={{ animationDelay: '0.35s' }}
              >
                <img
                  src={heroRetail}
                  alt=""
                  className="w-full h-[220px] xl:h-[260px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <span className="absolute bottom-3 left-3 text-[11px] font-semibold text-white/90 tracking-wide">
                  Retail &amp; jewellery façades
                </span>
              </div>

              <div
                className="absolute -right-2 xl:right-0 top-[22%] w-[42%] max-w-[200px] rounded-xl overflow-hidden ring-1 ring-white/20 shadow-xl animate-float-slow"
                style={{ animationDelay: '1.15s' }}
              >
                <img src={heroGate} alt="" className="w-full h-[120px] xl:h-[140px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <span className="absolute bottom-2 left-2 right-2 text-[9px] font-bold uppercase tracking-wider text-brand-amber">
                  Builder &amp; gates
                </span>
              </div>

              <div
                className="absolute -left-1 xl:-left-2 bottom-[6%] w-[46%] max-w-[210px] rounded-xl overflow-hidden ring-1 ring-white/20 shadow-xl animate-float-slow"
                style={{ animationDelay: '2s' }}
              >
                <img src={heroGlow} alt="" className="w-full h-[115px] xl:h-[130px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                <span className="absolute bottom-2 left-2 right-2 text-[9px] font-bold uppercase tracking-wider text-brand-yellow">
                  Night visibility
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-amber/40 to-transparent"
        aria-hidden
      />
    </section>
  );
}

function StatCard({ label, value, sub, delay, visible }) {
  return (
    <div
      className={[
        'text-center p-6 rounded-2xl border border-white/10 bg-surface-elevated/60 backdrop-blur-sm',
        visible ? 'animate-fade-up' : 'opacity-0',
      ].join(' ')}
      style={{ animationDelay: `${delay}ms` }}
    >
      <p className="text-3xl md:text-4xl font-bold text-gradient-brand mb-1">{value}</p>
      <p className="text-white font-semibold text-sm">{label}</p>
      {sub ? <p className="text-gray-500 text-xs mt-2">{sub}</p> : null}
    </div>
  );
}

function About() {
  const [philRef, philVisible] = useSectionReveal();
  const [statsRef, statsVisible] = useSectionReveal();
  const [expRef, expVisible] = useSectionReveal();
  const [mfgRef, mfgVisible] = useSectionReveal();
  const [svcRef, svcVisible] = useSectionReveal();
  const [whyRef, whyVisible] = useSectionReveal();
  const [locRef, locVisible] = useSectionReveal();

  return (
    <div className="bg-surface">
      <AboutHero />

      {/* Philosophy */}
      <section ref={philRef} className="relative py-16 md:py-24 border-t border-white/5 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-40 bg-[radial-gradient(ellipse_80%_50%_at_20%_50%,rgba(234,88,12,0.08),transparent_60%)]"
          aria-hidden
        />
        <div className="container mx-auto px-4 relative z-10 max-w-6xl">
          <div
            className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start ${
              philVisible ? 'animate-fade-up' : 'opacity-0'
            }`}
          >
            <div>
              <p className="text-brand-amber text-xs font-semibold uppercase tracking-[0.2em] mb-4">
                Philosophy
              </p>
              <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight mb-6">
                A sign isn&apos;t decoration — it&apos;s{' '}
                <span className="text-gradient-brand">your first handshake</span>
              </h2>
            </div>
            <div className="space-y-5 text-gray-400 leading-relaxed text-base md:text-lg">
              <p>
                At Shyam Advertising, we treat every board as the face of your business — the first thing
                people notice. We design and install work that is visually strong, professionally finished,
                and built to last in Mumbai weather.
              </p>
              <p>
                From compact shop fronts to large gate and terrace identities, we aim for visibility, clean
                branding, and a finish that holds up on site.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-12 md:py-16 border-t border-white/5 bg-surface-elevated/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            <StatCard label="Years in signage" value="25+" sub="And counting" delay={0} visible={statsVisible} />
            <StatCard label="Builder-scale jobs" value="10+" sub="Terrace, gate, site" delay={80} visible={statsVisible} />
            <StatCard label="Base" value="Kandivali" sub="Serving all Mumbai" delay={160} visible={statsVisible} />
            <StatCard label="Process" value="Design → install" sub="In-house build" delay={240} visible={statsVisible} />
          </div>
        </div>
      </section>

      {/* Experience / brands */}
      <section ref={expRef} className="py-16 md:py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className={`max-w-3xl mb-10 ${expVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            <p className="text-brand-amber text-xs font-semibold uppercase tracking-[0.2em] mb-3">Experience</p>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Trusted on sites you{' '}
              <span className="text-gradient-brand">already know</span>
            </h2>
            <p className="text-gray-400 leading-relaxed">
              We&apos;ve supplied and installed signage for recognised names and repeat builder programmes —
              the kind of work where finish and timelines matter.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mb-8">
            {brands.map((brand, idx) => (
              <span
                key={brand}
                className={[
                  'px-4 py-2 rounded-full text-sm font-medium border border-white/10 bg-white/[0.04]',
                  'hover:border-brand-amber/40 hover:bg-brand-orange/10 transition-all duration-300',
                  expVisible ? 'animate-fade-up' : 'opacity-0',
                ].join(' ')}
                style={{ animationDelay: expVisible ? `${80 + idx * 70}ms` : undefined }}
              >
                {brand}
              </span>
            ))}
          </div>
          <p
            className={`text-gray-400 leading-relaxed max-w-3xl ${
              expVisible ? 'animate-fade-up' : 'opacity-0'
            }`}
            style={{ animationDelay: expVisible ? '420ms' : undefined }}
          >
            Alongside these brands, we&apos;ve delivered terrace signage, building gates, and large-format site
            branding — experience that transfers directly to your next façade or retail rollout.
          </p>
        </div>
      </section>

      {/* Manufacturing */}
      <section ref={mfgRef} className="py-16 md:py-24 border-t border-white/5">
        <div className="container mx-auto px-4 max-w-6xl">
          <div
            className={[
              'relative rounded-3xl border border-white/10 overflow-hidden',
              'bg-gradient-to-br from-surface-elevated via-surface to-surface-elevated/80',
              'shadow-card-premium p-8 md:p-12 lg:p-14',
              mfgVisible ? 'animate-fade-up' : 'opacity-0',
            ].join(' ')}
          >
            <div
              className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-orange via-brand-amber to-brand-yellow opacity-90"
              aria-hidden
            />
            <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-12">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-brand flex items-center justify-center shadow-brand-glow">
                <Factory className="w-7 h-7 text-black" aria-hidden />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Manufacturing <span className="text-gradient-brand">& process</span>
                </h2>
                <p className="text-gray-500 text-sm mb-6">Controlled quality from the shop floor to the lift</p>
                <div className="space-y-4 text-gray-400 leading-relaxed">
                  <p>
                    Most production runs through our <span className="text-white font-medium">in-house</span>{' '}
                    setup so we can guard tolerances, finishing, and delivery dates.
                  </p>
                  <p>
                    For specialised materials and technical scopes, we work with vetted partners — the same
                    discipline we apply in-house, extended through trusted fabrication networks.
                  </p>
                  <p>
                    From design intent and material selection through fabrication and final installation, we
                    keep the thread unbroken so nothing gets lost between teams.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section ref={svcRef} className="py-16 md:py-24 border-t border-white/5 relative">
        <div
          className="pointer-events-none absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_60%_40%_at_100%_0%,rgba(250,204,21,0.06),transparent_50%)]"
          aria-hidden
        />
        <div className="container mx-auto px-4 relative z-10 max-w-6xl">
          <div className={`max-w-3xl mb-12 ${svcVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            <p className="text-brand-amber text-xs font-semibold uppercase tracking-[0.2em] mb-3">
              Capabilities
            </p>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              What we <span className="text-gradient-brand">fabricate & install</span>
            </h2>
            <p className="text-gray-400">
              A practical checklist of signage families we work with every week. For visuals, jump to{' '}
              <Link to="/services" className="text-brand-amber hover:text-brand-yellow underline-offset-2 hover:underline">
                Services
              </Link>
              .
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {servicesList.map((service, idx) => (
              <div
                key={service}
                className={[
                  'group flex items-start gap-3 p-4 rounded-2xl border border-white/10 bg-surface-elevated/40',
                  'hover:border-brand-amber/35 hover:bg-white/[0.03] hover:shadow-[0_12px_40px_-20px_rgba(234,88,12,0.25)]',
                  'transition-all duration-300',
                  svcVisible ? 'animate-fade-up' : 'opacity-0',
                ].join(' ')}
                style={{ animationDelay: svcVisible ? `${idx * 45}ms` : undefined }}
              >
                <CheckCircle className="w-5 h-5 text-brand-amber shrink-0 mt-0.5 group-hover:text-brand-yellow transition-colors" />
                <span className="text-gray-300 text-sm leading-snug">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section ref={whyRef} className="py-16 md:py-24 border-t border-white/5 bg-surface-elevated/20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className={`text-center max-w-2xl mx-auto mb-14 ${whyVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            <p className="text-brand-amber text-xs font-semibold uppercase tracking-[0.2em] mb-3">Why us</p>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Reasons teams <span className="text-gradient-brand">stay with us</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyChooseList.map((item, idx) => (
              <div
                key={item}
                className={[
                  'relative p-6 rounded-2xl border border-white/10 bg-surface/80 overflow-hidden',
                  'hover:-translate-y-1 hover:border-brand-amber/30 hover:shadow-card-premium-hover transition-all duration-500',
                  whyVisible ? 'animate-fade-up' : 'opacity-0',
                ].join(' ')}
                style={{ animationDelay: whyVisible ? `${idx * 75}ms` : undefined }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-orange to-brand-yellow rounded-full" />
                <div className="flex items-start gap-3 pl-2">
                  <Award className="w-5 h-5 text-brand-yellow shrink-0" aria-hidden />
                  <span className="text-gray-200 text-sm md:text-base leading-relaxed">{item}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & contact */}
      <section ref={locRef} className="py-16 md:py-28 border-t border-white/5 pb-24">
        <div className="container mx-auto px-4 max-w-6xl">
          <div
            className={[
              'grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-stretch',
              locVisible ? 'animate-fade-up' : 'opacity-0',
            ].join(' ')}
          >
            <div className="rounded-3xl border border-white/10 bg-surface-elevated/60 p-8 md:p-10 flex flex-col justify-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                <span className="text-gradient-brand">Location</span>
              </h2>
              <div className="flex items-start gap-3 mb-4">
                <MapPin className="w-6 h-6 text-brand-amber shrink-0 mt-0.5" aria-hidden />
                <p className="text-gray-300 leading-relaxed">
                  Kandivali, Mumbai — we regularly install across the city for retail, offices, and builder
                  programmes.
                </p>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">
                Shops, showrooms, cafés, corporate offices, and large-scale site branding — one team from
                drawing to final fix.
              </p>
            </div>

            <div className="rounded-3xl border border-white/10 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/15 via-transparent to-brand-yellow/10 pointer-events-none" />
              <div className="relative p-8 md:p-10 h-full flex flex-col">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Let&apos;s <span className="text-gradient-brand">talk</span>
                </h2>
                <p className="text-gray-400 leading-relaxed mb-8 flex-grow">
                  Ready for a sharper frontage or a full branding pass? Send a brief — we&apos;ll respond with
                  options and next steps.
                </p>
                <div className="space-y-4">
                  <a
                    href="tel:9821466536"
                    className="flex items-center gap-3 text-gray-200 hover:text-brand-yellow transition-colors"
                  >
                    <Phone className="w-5 h-5 text-brand-amber shrink-0" aria-hidden />
                    <span className="font-semibold">9821466536</span>
                  </a>
                  <a
                    href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors text-sm font-medium"
                  >
                    <FaWhatsapp className="w-5 h-5" aria-hidden />
                    WhatsApp same number
                  </a>
                </div>
                <Link
                  to="/contact"
                  className="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-xl bg-gradient-brand px-6 py-3.5 text-sm font-bold text-black hover:opacity-95 transition-opacity shadow-brand-glow"
                >
                  Open contact form
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </Link>

                <div className="flex flex-wrap gap-3 mt-10 pt-8 border-t border-white/10">
                  <a
                    href="https://facebook.com/shamadvertising101"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/5 border border-white/10 hover:bg-brand-orange/20 hover:border-brand-amber/40 transition-all"
                    aria-label="Facebook"
                  >
                    <FiFacebook className="w-5 h-5 text-white" />
                  </a>
                  <a
                    href="https://instagram.com/shyam_advertising_101"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/5 border border-white/10 hover:bg-brand-orange/20 hover:border-brand-amber/40 transition-all"
                    aria-label="Instagram"
                  >
                    <FiInstagram className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
